import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchTimedLyrics } from '../../../../services/LyricsService';
import { updateSongLyrics } from '../../../../services/RepertoireService';
import { WARMUPS_SONG_ID } from '../../../../services/songNotesModel';

/**
 * Lyrics for the current song — extracted from SongNotes.js loadLyricsForSong
 * + the edit-draft state.
 *
 * Lyrics belong to the SONG, not to a lesson or a note: they persist on the
 * repertoire doc, so the next teacher opening this song already has them.
 * Warm-ups (sentinel, no repertoire doc) never fetches or persists.
 */
const useLyrics = ({ currentSong, setSongs, isPersistent, uid = null }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [editMode, setEditMode] = useState(false);
	const [draft, setDraft] = useState('');
	// 'idle' | 'fetching' | 'none' | 'failed' (pass 4 S2.5): "no words found"
	// is not a failure and never says "tell Anthony"; a fetch that broke is.
	const [status, setStatus] = useState('idle');
	const [failDetail, setFailDetail] = useState('');
	// Wrong lyrics → Paste the right words: the save REPLACES the song's words
	// and drops their timing (it belonged to the wrong song).
	const [replacing, setReplacing] = useState(false);
	// lrclib ids already shown for a song this session (Search again skips them).
	const shownRef = useRef(new Map());
	// Songs already asked about this session (pass 4 S1): the automatic fetch
	// runs once per song, so a song with no words anywhere is not re-searched
	// on every visit, and a tap on Find lyrics is never doubled by it.
	const triedRef = useRef(new Set());
	const inFlightRef = useRef(new Set());

	// Reset the draft when the song changes.
	useEffect(() => {
		setDraft(currentSong?.lyrics?.text || '');
		setError('');
		setEditMode(false);
		setReplacing(false);
		setStatus('idle');
	}, [currentSong?.id, currentSong?.lyrics?.text]);

	const patchSongLyrics = useCallback(
		(songId, lyricsData) => {
			setSongs((prev) => prev.map((s) => (s.id === songId ? { ...s, lyrics: lyricsData } : s)));
		},
		[setSongs]
	);

	/**
	 * Fetch the words, WITH their timing when lrclib has it (pass 4 S1,
	 * 2026-10-02), else plain from every source. Metadata title/artist preferred.
	 */
	const load = useCallback(async () => {
		const song = currentSong;
		if (!song || song.id === WARMUPS_SONG_ID || song.lyrics?.text) return;
		if (inFlightRef.current.has(song.id)) return;
		inFlightRef.current.add(song.id);
		triedRef.current.add(song.id);
		setError('');
		setIsLoading(true);
		setStatus('fetching');
		try {
			const title = song.metadata?.title || song.name;
			const artist = song.metadata?.artist || '';
			const lyricsData = await fetchTimedLyrics(title, artist);
			if (lyricsData) {
				rememberShown(song.id, lyricsData);
				setStatus('idle');
				patchSongLyrics(song.id, lyricsData);
				setDraft(lyricsData.text);
				if (isPersistent) {
					updateSongLyrics(song.id, lyricsData).catch((err) =>
						console.error('Error saving lyrics to repertoire:', err)
					);
				}
			} else {
				setStatus('none');
			}
		} catch (err) {
			setStatus('failed');
			setFailDetail(String((err && err.message) || err));
			console.error('Lyrics load error', err);
		} finally {
			inFlightRef.current.delete(song.id);
			setIsLoading(false);
		}
	}, [currentSong, isPersistent, patchSongLyrics]);

	// Words arrive by themselves (pass 4 S1): the first time a song with none
	// is open this session, at every width. Before 2026-10-02 only the phone /
	// iPad sheet fetched on its own and the desktop column waited for Find
	// lyrics.
	useEffect(() => {
		const song = currentSong;
		if (!song || song.id === WARMUPS_SONG_ID || song.lyrics?.text) return;
		if (triedRef.current.has(song.id)) return;
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentSong?.id, currentSong?.lyrics?.text]);

	const rememberShown = (songId, l) => {
		const id = l && l.timing && l.timing.lrclibId;
		if (!id) return;
		const set = shownRef.current.get(songId) || new Set();
		set.add(id);
		shownRef.current.set(songId, set);
	};

	/**
	 * Wrong lyrics → Search again (pass 4, 2026-10-02): the next same-title
	 * synced match lrclib has, skipping every one already shown for this song.
	 * Nothing else found leaves the words as they are and says so.
	 */
	const searchAgain = useCallback(async () => {
		const song = currentSong;
		if (!song || song.id === WARMUPS_SONG_ID) return;
		const shown = new Set(shownRef.current.get(song.id) || []);
		if (song.lyrics?.timing?.lrclibId) shown.add(song.lyrics.timing.lrclibId);
		setIsLoading(true);
		setStatus('fetching');
		setError('');
		try {
			const title = song.metadata?.title || song.name;
			const next = await fetchTimedLyrics(title, song.metadata?.artist || '', { exclude: [...shown], again: true });
			if (!next) {
				setStatus('idle');
				setError('No other match found. Paste the right words instead.');
				return;
			}
			rememberShown(song.id, next);
			patchSongLyrics(song.id, next);
			setDraft(next.text);
			setStatus('idle');
			if (isPersistent) await updateSongLyrics(song.id, next);
		} catch (err) {
			setStatus('failed');
			setFailDetail(String((err && err.message) || err));
		} finally {
			setIsLoading(false);
		}
	}, [currentSong, isPersistent, patchSongLyrics]);

	/**
	 * The studio Mac heard different words in the lyric video (lyrics.check,
	 * 2026-10-02). Accept: they become the song's words with that video's
	 * timing, verified by the teacher who accepted them (Anthony: once
	 * corrected they're verified). `textFromJob` carries the job id so the
	 * write does not queue another check of the same words.
	 */
	const acceptHeard = useCallback(async () => {
		const song = currentSong;
		const check = song?.lyrics?.check;
		if (!song || !check || !check.text) return;
		const next = {
			text: check.text,
			source: check.method === 'transcribed' ? 'heard' : 'LRCLIB',
			timed: check.timed || null,
			timing: { method: check.method, from: 'video', videoUrl: check.videoUrl || null, checked: true, lrclibId: check.lrclibId || null, jobId: check.jobId || null },
			verified: { by: uid, at: new Date().toISOString(), how: 'heard' },
			textFromJob: check.jobId || null,
		};
		patchSongLyrics(song.id, next);
		setDraft(next.text);
		if (isPersistent) await updateSongLyrics(song.id, next);
	}, [currentSong, isPersistent, patchSongLyrics, uid]);

	/** Keep the song's words; the note goes away for this video. */
	const keepWords = useCallback(async () => {
		const song = currentSong;
		if (!song || !song.lyrics || !song.lyrics.check) return;
		const next = { ...song.lyrics, check: { ...song.lyrics.check, dismissed: true } };
		patchSongLyrics(song.id, next);
		if (isPersistent) await updateSongLyrics(song.id, next);
	}, [currentSong, isPersistent, patchSongLyrics]);

	/** Wrong lyrics → Paste the right words: the editor, emptied, replacing. */
	const pasteRight = useCallback(() => {
		setReplacing(true);
		setDraft('');
		setEditMode(true);
	}, []);

	/** Commit a manual edit/paste of the draft. */
	const saveDraft = useCallback(async () => {
		const song = currentSong;
		if (!song || song.id === WARMUPS_SONG_ID) return;
		const text = draft.trim();
		// A replacement (Wrong lyrics) keeps nothing of the wrong song: no
		// timing, and the words count as checked by the teacher who pasted
		// them (Anthony, 2026-10-02: once corrected they're verified). An
		// ordinary edit keeps the timing; lines match by words, so a fixed
		// typo still follows.
		const lyricsData = replacing
			? { text, source: 'manual', timed: null, timing: null, verified: { by: uid, at: new Date().toISOString(), how: 'pasted' } }
			: { ...(song.lyrics || {}), text, source: song.lyrics?.source || 'manual' };
		setReplacing(false);
		patchSongLyrics(song.id, lyricsData);
		setEditMode(false);
		if (isPersistent && text) {
			try {
				await updateSongLyrics(song.id, lyricsData);
			} catch (err) {
				console.error('Error saving lyrics to repertoire:', err);
				setError('Lyrics did not save to the song. They are still on screen — try again.');
			}
		}
	}, [currentSong, draft, isPersistent, patchSongLyrics, replacing, uid]);

	return {
		lyrics: currentSong?.lyrics || null,
		isLoading,
		error,
		editMode,
		setEditMode,
		draft,
		setDraft,
		load,
		saveDraft,
		status,
		failDetail,
		replacing,
		searchAgain,
		pasteRight,
		acceptHeard,
		keepWords,
	};
};

export default useLyrics;
